import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { ListItemNode, ListNode } from '@lexical/list'
import { AutoLinkNode, LinkNode } from '@lexical/link'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { useLexicalIsTextContentEmpty } from '@lexical/react/useLexicalIsTextContentEmpty'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import {
  BLUR_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  $getRoot,
  $insertNodes,
  $setSelection,
} from 'lexical'
import { twMerge as mergeClassNames } from 'tailwind-merge'

// Components
import CustomAutoLinkPlugin from './AutoLinkPlugin'
import Toolbar from './Toolbar'

/**
 * Plugin that monitors changes to the editor state.
 */
const OnChangePlugin = ({ onChange, initialHtml }) => {
  const [editor] = useLexicalComposerContext()

  // State
  const [firstRender, setFirstRender] = useState(true)

  useEffect(() => {
    if (!initialHtml || !firstRender) return

    if (editor) {
      setFirstRender(false)

      // Insert initial HTML
      editor.update(() => {
        const parser = new DOMParser()
        const dom = parser.parseFromString(initialHtml, 'text/html')
        const nodes = $generateNodesFromDOM(editor, dom)
        $insertNodes(nodes)

        // Clear focus from editor
        $setSelection(null)
      })

      // Register update listener
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const htmlString = $generateHtmlFromNodes(editor, null)
          onChange(htmlString)
        })
      })
    }
  }, [editor])

  useEffect(() => {}, [editor])
}

OnChangePlugin.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialHtml: PropTypes.string.isRequired,
}

/**
 * Plugin that monitors changes to the editor state when focus leaves input.
 */
const EditorBlurPlugin = ({ defaultValue }) => {
  // Context
  const [editor] = useLexicalComposerContext()
  const isTextContentEmpty = useLexicalIsTextContentEmpty(editor)

  // State
  const [firstRender, setFirstRender] = useState(true)

  useEffect(() => {
    if (defaultValue) {
      editor.registerCommand(
        BLUR_COMMAND,
        () => {
          if (isTextContentEmpty && !firstRender) {
            editor.update(() => {
              // Remove all current nodes
              const root = $getRoot()
              root.clear()

              // Reset to default value
              const parser = new DOMParser()
              const dom = parser.parseFromString(defaultValue, 'text/html')
              const nodes = $generateNodesFromDOM(editor, dom)
              $insertNodes(nodes)
            })
          }

          setFirstRender(false)
        },
        COMMAND_PRIORITY_EDITOR,
      )
    }
  }, [])

  return null
}

EditorBlurPlugin.propTypes = {
  defaultValue: PropTypes.string.isRequired,
}

/**
 * Plugin that displays a reset button.
 * - Resets the editor state to the default value.
 */
const ResetPlugin = ({ buttonLabel, defaultValue }) => {
  // Context
  const [editor] = useLexicalComposerContext()

  return (
    <button
      className="mt-2 self-end text-xs text-purple"
      onClick={() => {
        if (editor) {
          editor.update(() => {
            // Remove all current nodes
            const root = $getRoot()
            root.clear()

            // Reset to default value
            const parser = new DOMParser()
            const dom = parser.parseFromString(defaultValue, 'text/html')
            const nodes = $generateNodesFromDOM(editor, dom)
            $insertNodes(nodes)
          })
        }
      }}
      type="button"
    >
      {buttonLabel}
    </button>
  )
}

ResetPlugin.propTypes = {
  buttonLabel: PropTypes.string.isRequired,
  defaultValue: PropTypes.string.isRequired,
}

/**
 *
 * RichTextInput
 *
 */
const RichTextInput = ({
  className,
  defaultValue,
  disabled,
  historyEnabled,
  id,
  inputStyles,
  onChange,
  placeholder,
  resetOnBlur,
  toolbarStyles,
  value,
}) => {
  /**
   * Handles changes to the editor state.
   * - Passes HTML string to `onChange`
   */
  const handleChange = useCallback(
    (html) => {
      // Only call onChange if the value has changed
      if (html !== value) {
        onChange(html)
      }
    },
    [onChange],
  )

  return (
    <LexicalComposer
      initialConfig={{
        editable: !disabled,
        // Default to undefined, we'll handle loading the initial value in the OnChangePlugin
        editorState: undefined,
        theme: {
          link: 'editor-link',
          list: {
            ul: 'editor-list-ul',
            listitem: 'editor-listitem',
            nested: {
              listitem: 'editor-nestedListItem',
            },
            olDepth: ['editor-ol1', 'editor-ol2', 'editor-ol3', 'editor-ol4', 'editor-ol5'],
          },
          text: {
            underline: 'editor-text-underline',
          },
        },
        onError(error) {
          // eslint-disable-next-line no-console
          console.log('Rich Text Error:', error)
        },
        nodes: [AutoLinkNode, ListNode, ListItemNode, LinkNode],
      }}
      key={`rich-text-${id}`}
    >
      <div
        className={mergeClassNames(
          'text-md h-[150px] rounded-2xl border border-gray-550 bg-white text-start font-nunito sm:text-base',
          className,
        )}
      >
        <Toolbar className={toolbarStyles} historyEnabled={historyEnabled} />

        <div className={mergeClassNames('relative h-[100px] p-2', inputStyles)}>
          <RichTextPlugin
            contentEditable={<ContentEditable className="h-full overflow-y-auto outline-none" />}
            placeholder={
              <span className="pointer-events-none absolute top-2 text-gray-500">
                {placeholder}
              </span>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          {historyEnabled && <HistoryPlugin />}
          <ListPlugin />
          <LinkPlugin />
          <TabIndentationPlugin />
          <CustomAutoLinkPlugin />
          <OnChangePlugin onChange={handleChange} initialHtml={value} />
          {resetOnBlur && <EditorBlurPlugin defaultValue={defaultValue} />}
        </div>
      </div>

      {defaultValue && <ResetPlugin buttonLabel="Reset to Default" defaultValue={defaultValue} />}
    </LexicalComposer>
  )
}

RichTextInput.defaultProps = {
  className: null,
  defaultValue: null,
  disabled: false,
  historyEnabled: false,
  inputStyles: null,
  toolbarStyles: null,
  resetOnBlur: false,
  value: null,
}

RichTextInput.propTypes = {
  className: PropTypes.string,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  historyEnabled: PropTypes.bool,
  id: PropTypes.string.isRequired,
  inputStyles: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  resetOnBlur: PropTypes.bool,
  toolbarStyles: PropTypes.string,
  value: PropTypes.string,
}

export default RichTextInput
